<template>
  <div class="p-float-label dropdown-wrapper view-button-input">
    <CustomDropdown :id="id"
                    ref="dd"
                    data-key="id"
                    :disabled="isDisabled"
                    class="dropdown__select"
                    :class="selectClasses"
                    v-model="localValue"
                    :useMinSearchChars="true"
                    :options="filteredCustomers"
                    :filterPlaceholder="$t('searchCustomerPlaceholder')"
                    :filterFields="filterFields"
                    :filter="true"
                    :clearSearchData="!visible || clearSearchData"
                    :createButton="showCreateButton"
                    :loading="customersDataIsLoading || loading"
                    @change="emitChange($event)"
                    @filter="searchCustomers"
                    @create-new="createCustomer"
                    :showClear="showClear">
      <template #value="slotProps">
        <div v-if="slotProps.value" class="p-d-flex p-ai-center">
          <CustomerStatusHistoryButton
              :customerStatus="slotProps.value.customerStatus"
              :isChangeable="false"/>

          <div v-if="isCompany(slotProps.value) && slotProps.value.company_name">
            {{ slotProps.value.company_name }}
            <span v-show="slotProps.value.reg_number">
              ({{ slotProps.value.reg_number }})
            </span>
          </div>

          <div v-else-if="slotProps.value.first_name || slotProps.value.last_name" class="p-d-flex p-ai-center">
            {{ slotProps.value.first_name }} {{ slotProps.value.last_name }}
            <UserLanguageBadge :language="slotProps.value.language"/>
          </div>
        </div>

        <span v-else style="visibility:hidden">.</span>
      </template>

      <template #option="slotProps">
        <div v-if="slotProps.option" class="p-d-flex p-ai-center p-jc-between">
          <div class="p-d-flex p-ai-center">
            <CustomerStatusHistoryButton :customerStatus="slotProps.option.customerStatus"
                                         :isChangeable="false"/>
            <span v-if="isCompany(slotProps.option) && slotProps.option.company_name">
              {{ slotProps.option.company_name }}
              <span v-show="slotProps.option.reg_number">
                ({{ slotProps.option.reg_number }})
              </span>
            </span>

            <div v-else-if="slotProps.option.first_name || slotProps.option.last_name" class="p-d-flex p-ai-center">
              {{ slotProps.option.first_name }} {{ slotProps.option.last_name }}
              <UserLanguageBadge :language="slotProps.option.language"/>
            </div>
          </div>

          <!--          <div class="p-ml-1" v-if="isCompany(slotProps.option) && slotProps.option.company_name">-->
          <!--            <span v-if="slotProps.option.company_phone_code && slotProps.option.company_phone_number">-->
          <!--              +{{ slotProps.option.company_phone_code }} {{ slotProps.option.company_phone_number }}</span>-->
          <!--          </div>-->
          <!--          <div class="p-ml-1" v-else>-->
          <div class="p-ml-1" v-if="!isCompany(slotProps.option) && slotProps.option.phone_code && slotProps.option.phone_number">
            +{{ slotProps.option.phone_code }} {{ slotProps.option.phone_number }}
          </div>
        </div>
      </template>
    </CustomDropdown>

    <div class="dropdown__buttons" v-show="!isDisabled && canShowButtons">
      <Button type="button"
              v-if="showEditButton && localValue"
              @click="editCustomer"
              class="dropdown__edit-button p-d-flex p-ai-center p-jc-center"
              icon="ti-pencil"/>

      <Button type="button"
              v-if="showCreateButton"
              @click="createCustomer"
              class="dropdown__create-button p-d-flex p-ai-center p-jc-center"
              icon="ti-plus"/>
    </div>

    <label :for="id" class="view-button-input__label">
      <span class="view-button-input__inner">
        <span class="view-button-input__text">
          {{ label }}<span v-if="countLabel && +countLabel"> ({{ countLabel }})</span>
          <span class="warning-color" v-if="required">*</span>
        </span>
        <InputViewLink :uid="localValue?.uid" :path="'customers'"/>
      </span>
    </label>
  </div>

  <small class="p-invalid" v-if="isWrong">{{ $t('Required field') }}</small>
</template>

<script>
import InputViewLink from "@/components/InputViewLink.vue";
import httpClient from "@/services/http.services";
import CustomerStatusHistoryButton from "@/pages/users/components/CustomerStatusHistoryButton.vue";
import UserLanguageBadge from "@/components/UserLanguageBadge.vue";
import ShowErrorsMixins from "@/mixins/showErrorsMixins";
import constants from "@/constants";

export default {
  name: 'CustomerDropdown',
  mixins: [ShowErrorsMixins],
  components: { InputViewLink, CustomerStatusHistoryButton, UserLanguageBadge },

  props: {
    id: { type: String, default: 'order-modal-customer' },
    modelValue: { type: Object, default: null },

    disabled: { type: Boolean, default: false },

    visible: { type: Boolean, default: true },

    label: { type: String, default: 'Customer' },
    onlyIndividuals: { type: Boolean, default: false },
    isWrong: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },

    excludeUserId: {
      type: [Number, String],
      default: null,
    },

    showCreateButton: { type: Boolean, default: true },
    showEditButton: { type: Boolean, default: true },
    relatedCustomer: { type: Object, default: null },
  },

  emits: ['update:modelValue', 'change', 'create', 'edit'],

  data() {
    return {
      customers: [],
      companyContactPersons: [],
      contactPersonCompanies: [],
      customersDataIsLoading: false,
      customersApiRequestId: 0,
      customerSearchData: '',
      clearSearchData: false,
    };
  },

  beforeUnmount() {
    this.invalidateRequests();
  },

  watch: {
    visible: {
      flush: 'sync',
      handler(v) {
        if (!v) this.reset({ clearModel: false });
      },
    },

    relatedCustomer: {
      immediate: true,
      flush: 'sync',
      handler(customer, previousCustomer) {
        const changed = this.idKey(customer?.id) !== this.idKey(previousCustomer?.id);
        if (changed) this.invalidateRequests();
        if (changed || !customer || !this.isCompany(customer)) {
          this.companyContactPersons = [];
          this.contactPersonCompanies = [];
          this.customers = this.localValue ? [{ ...this.localValue }] : [];

          this.customerSearchData = '';
          this.clearSearchData = true;
          this.$refs.dd?.clearFilterValue?.();
        }
      },
    },
    excludeUserId(newId) {
      if (!newId) return;

      const exKey = this.idKey(newId);

      if (this.customers?.length) {
        this.customers = this.customers.filter(u => this.idKey(u?.id) !== exKey);
      }

      if (this.localValue?.id && this.idKey(this.localValue.id) === exKey) {
        this.localValue = null;
        this.$emit("update:modelValue", null);
        this.$emit("change", null);
      }
    },

    modelValue: {
      immediate: true,
      flush: 'sync',
      handler(v, previousValue) {
        if (this.idKey(v?.id) !== this.idKey(previousValue?.id)) this.invalidateRequests();
        if (!v || typeof v !== "object") return;
        this.upsertOption(v);
      },
    },
  },

  computed: {
    localValue: {
      get() { return this.modelValue; },
      set(v) { this.$emit('update:modelValue', v); },
    },

    countLabel() {
      if (this.companyContactPersons?.length) return this.companyContactPersons.length;
      if (this.contactPersonCompanies?.length) return this.contactPersonCompanies.length;
      return null;
    },

    filteredCustomers() {
      const ex = this.excludeUserId;
      if (!ex) return this.customers;

      const exKey = this.idKey(ex);
      return (this.customers || []).filter(u => this.idKey(u?.id) !== exKey);
    },

    isDisabled() {
      return !!this.disabled;
    },

    showClear() {
      return !!this.localValue && !this.isDisabled;
    },

    canShowButtons() {
      return !!this.showCreateButton || !!this.showEditButton;
    },

    filterFields() {
      return [
        "first_name",
        "last_name",
        "email",
        "phone_number",
        "phone_code",
        "full_phone_number",
        "company_name",
        "reg_number",
      ];
    },

    selectClasses() {
      return {
        "dropdown__select--border-radius": this.disabled,
        "dropdown__select--one-button-width":
            !this.disabled && (!this.canShowButtons || !this.localValue),
        "dropdown__select--two-buttons-width":
            !this.disabled && this.canShowButtons && !!this.localValue,
        "p-invalid": this.isWrong,
      };
    },
  },

  methods: {
    invalidateRequests() {
      // Never reuse request IDs: an older response may still be in flight.
      this.customersApiRequestId++;
      this.customersDataIsLoading = false;
    },

    idKey(v) {
      return v === null || v === undefined ? "" : String(v);
    },

    typeKey(v) {
      return v === null || v === undefined ? "" : String(v);
    },

    isCompany(c) {
      return this.typeKey(c?.type) === String(constants.userTypes.legal_entity);
    },

    upsertOption(user) {
      if (!user) return;

      const key = this.idKey(user.id);
      const idx = this.customers.findIndex(c => this.idKey(c?.id) === key);

      if (idx === -1) {
        this.customers = [...this.customers, { ...user }];
      } else {
        const copy = [...this.customers];
        copy[idx] = { ...user };
        this.customers = copy;
      }
    },

    composeCompanyContactPersonsOptions(selected = undefined) {
      let options = Array.isArray(this.companyContactPersons) ? [...this.companyContactPersons] : [];
      const v = selected === undefined ? this.localValue : selected;

      if (v?.id) {
        const selKey = this.idKey(v.id);
        if (!options.some(u => this.idKey(u?.id) === selKey)) {
          options = [v, ...options];
        }
      }

      return options;
    },

    composeContactPersonCompaniesOptions(selected = undefined) {
      let options = Array.isArray(this.contactPersonCompanies) ? [...this.contactPersonCompanies] : [];
      const v = selected === undefined ? this.localValue : selected;

      if (v?.id) {
        const selKey = this.idKey(v.id);
        if (!options.some(u => this.idKey(u?.id) === selKey)) {
          options = [v, ...options];
        }
      }

      return options;
    },

    setCompanyContactPersons(list = [], selected = undefined) {
      this.invalidateRequests();
      let result = Array.isArray(list) ? list : [];

      if (this.excludeUserId) {
        const exKey = this.idKey(this.excludeUserId);
        result = result.filter(u => this.idKey(u?.id) !== exKey);
      }

      this.companyContactPersons = result;
      this.contactPersonCompanies = [];
      this.customers = this.composeCompanyContactPersonsOptions(selected);

      this.customerSearchData = "";
      this.clearSearchData = true;
      this.$refs.dd?.clearFilterValue?.();
    },

    async getContactPersons({
                              preserveSelected = false,
                              preserveUnlistedSelected = false,
                              autoSelectPrimary = true,
                              preferredRepresentativeId = null,
                            } = {}) {
      this.invalidateRequests();
      const requestId = this.customersApiRequestId;
      const customerId = this.relatedCustomer?.id;
      const isCurrent = () => requestId === this.customersApiRequestId
          && this.visible && this.idKey(customerId) === this.idKey(this.relatedCustomer?.id);
      if (!this.visible || !this.$store.getters.userCanGetData) return [];

      if (!this.relatedCustomer || !this.isCompany(this.relatedCustomer)) {
        this.companyContactPersons = [];
        this.contactPersonCompanies = [];
        this.customers = [];
        this.customerSearchData = "";
        this.clearSearchData = true;
        this.$refs.dd?.clearFilterValue?.();

        this.localValue = null;
        this.$emit("update:modelValue", null);

        return [];
      }

      this.customersDataIsLoading = true;

      try {
        const { status, data } = await httpClient.post(
            'user/dropdown-contact-persons',
            { id: customerId }
        );

        if (!isCurrent()) return [];
        const list = (status === 200 && Array.isArray(data)) ? data : [];

        this.setCompanyContactPersons(list);

        const keepId = preferredRepresentativeId
            ? this.idKey(preferredRepresentativeId)
            : (this.localValue?.id ? this.idKey(this.localValue.id) : null);

        const matched = keepId
            ? list.find(p => this.idKey(p?.id) === keepId)
            : null;

        const preserved = matched || (preserveUnlistedSelected ? this.localValue : null);
        if (preserveSelected && preserved) {
          this.localValue = preserved;
          this.$emit("update:modelValue", preserved);
          return list;
        }

        this.localValue = null;
        this.$emit("update:modelValue", null);

        if (autoSelectPrimary && list.length) {
          const primary = list.find(p => +p.is_primary === 1) || null;
          const autoSelected = primary || (list.length === 1 ? list[0] : null);

          if (autoSelected) {
            this.localValue = autoSelected;
            this.$emit("update:modelValue", autoSelected);
          }
        }

        return list;
      } catch (err) {
        if (isCurrent()) this.showError(err);
        return [];
      } finally {
        if (isCurrent()) this.customersDataIsLoading = false;
      }
    },


    emitChange(event) {
      this.invalidateRequests();
      const value = event?.value ?? null;
      const hasSearch = !!(this.customerSearchData || '').trim();

      if (
          this.excludeUserId &&
          value?.id &&
          this.idKey(value.id) === this.idKey(this.excludeUserId)
      ) {
        this.localValue = null;

        if (!hasSearch) {
          this.customers = this.companyContactPersons?.length
              ? [...this.companyContactPersons]
              : this.contactPersonCompanies?.length
                  ? [...this.contactPersonCompanies]
                  : [];
        }

        this.$emit("change", null);
        return;
      }

      if (!value) {
        this.localValue = null;

        if (!hasSearch) {
          if (this.companyContactPersons?.length) {
            this.customers = [...this.companyContactPersons];
          } else if (this.contactPersonCompanies?.length) {
            this.customers = [...this.contactPersonCompanies];
          } else {
            this.customers = [];
          }
        }

        this.$emit("change", null);
        return;
      }

      if (!hasSearch) {
        if (this.companyContactPersons?.length) {
          this.customers = this.composeCompanyContactPersonsOptions(value);
        } else if (this.contactPersonCompanies?.length) {
          this.customers = this.composeContactPersonCompaniesOptions(value);
        } else {
          this.customers = [value];
        }
      }

      this.$emit("change", value);
    },

    async searchCustomers(event) {
      this.invalidateRequests();
      const requestId = this.customersApiRequestId;
      if (!this.visible || !this.$store.getters.userCanGetData) return;

      const value = (event?.value ?? "").toString();
      this.customerSearchData = value;

      if (!value) {
        this.customersDataIsLoading = false;

        if (this.companyContactPersons?.length) {
          this.customers = this.composeCompanyContactPersonsOptions();
        } else if (this.contactPersonCompanies?.length) {
          this.customers = this.composeContactPersonCompaniesOptions();
        } else {
          this.customers = this.localValue ? [this.localValue] : [];
        }
        return;
      }

      if (value.length < 3) {
        this.customersDataIsLoading = false;
        this.customers = [];
        return;
      }

      this.clearSearchData = false;

      this.customersDataIsLoading = true;

      const url =
          "user/dropdown-customers?expand=customerStatus" +
          "&search=" + encodeURIComponent(value) +
          "&onlyIndividuals=" + (this.onlyIndividuals ? 1 : 0);

      try {
        const { status, data } = await httpClient(url);

        if (requestId !== this.customersApiRequestId) return;

        let list = (status === 200 && Array.isArray(data)) ? data : [];

        if (this.excludeUserId) {
          const exKey = this.idKey(this.excludeUserId);
          list = list.filter(u => this.idKey(u?.id) !== exKey);
        }

        this.customers = list;
      } catch (err) {
        if (requestId === this.customersApiRequestId) {
          this.showError(err);
        }
      } finally {
        if (requestId === this.customersApiRequestId) {
          this.customersDataIsLoading = false;
        }
      }
    },

    createCustomer() {
      let customer = {};

      if (this.customerSearchData && isFinite(this.customerSearchData)) {
        customer = { searchedPhoneNumber: this.customerSearchData };
      }

      this.$store.commit("toggleSecondLayer", true);
      this.$emit("create", customer);
    },

    editCustomer() {
      if (!this.localValue) return;

      const customer = { ...this.localValue };
      this.$store.commit("toggleSecondLayer", true);
      this.$emit("edit", customer);
    },


    updateCustomer(user, { forceSelect = false, silent = false } = {}) {
      if (!user) return;
      this.invalidateRequests();

      const userKey = this.idKey(user.id);
      const isSelected = this.idKey(this.localValue?.id) === userKey;

      let nextCustomers = [...this.customers];
      const idx = nextCustomers.findIndex(c => this.idKey(c?.id) === userKey);

      if (idx !== -1) {
        nextCustomers[idx] = {
          ...nextCustomers[idx],
          ...user,
        };
      } else if (forceSelect || isSelected) {
        nextCustomers.push({ ...user });
      } else {
        return;
      }

      this.customers = nextCustomers;

      const selected = this.customers.find(c => this.idKey(c?.id) === userKey) || null;

      if (forceSelect || isSelected) {
        this.localValue = selected;

        if (silent) {
          this.$emit("update:modelValue", selected);
        } else {
          this.emitChange({ value: selected });
        }
      }
    },

    reset({ clearModel = false, silent = false } = {}) {
      this.invalidateRequests();

      this.customers = [];
      this.companyContactPersons = [];
      this.contactPersonCompanies = [];

      this.clearSearchData = true;
      this.$refs.dd?.clearFilterValue?.();
      this.customerSearchData = "";

      if (clearModel) {
        this.localValue = null;
        this.$emit("update:modelValue", null);

        if (!silent) {
          this.$emit("change", null);
        }
      }
    }
  },
};
</script>
